import { useEffect } from "react";
import { FaMapMarkerAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";

// ------------------------------------
// Google Places Component
// ------------------------------------
export const AddressAutocomplete = ({
  value,
  onChange,
  onSelect,
  disabled,
  name = "street",
}) => {
  const { t } = useTranslation();
  const {
    ready,
    value: inputValue,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    debounce: 500,
    defaultValue: value,
  });

  // Sync external value with internal autocomplete value
  useEffect(() => {
    if (value !== inputValue) {
      setValue(value || "", false); // false = do not trigger new suggestions
    }
  }, [value]);

  // Input handler
  const handleInput = (e) => {
    const newValue = e.target.value;
    setValue(newValue);
    if (onChange) {
      onChange(e);
    }
  };

  // When user selects suggestion
  const handleSelect = async (description) => {
    setValue(description, false);
    clearSuggestions();

    const geocode = await getGeocode({ address: description });
    const { lat, lng } = await getLatLng(geocode[0]);
    const address = geocode[0].address_components;

    const getComponent = (type) =>
      address.find((c) => c.types.includes(type))?.long_name || "";

    const city = getComponent("locality") || getComponent("sublocality");
    const state = getComponent("administrative_area_level_1");
    const country = getComponent("country");
    const zip_code =
      getComponent("postal_code") ||
      getComponent("postal_code_prefix") ||
      "";

    // Build complete street address with state
    let completeStreet = description;

    // If state exists and is not already in description, add it
    if (state && !description.includes(state)) {
      // Insert state before country if country exists in description
      if (country && description.includes(country)) {
        completeStreet = description.replace(country, `${state}, ${country}`);
      } else {
        completeStreet = `${description}, ${state}`;
      }
    }

    onSelect({
      street: completeStreet,
      city: city,
      state: state,
      country: country,
      zip_code: zip_code,
      latitude: lat,
      longitude: lng,
    });
  };

  // Get Current Location
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error(t("geolocation_not_supported"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;

        const geocode = await getGeocode({ location: { lat, lng } });
        const description = geocode[0].formatted_address;
        const address = geocode[0].address_components;

        const getComponent = (type) =>
          address.find((c) => c.types.includes(type))?.long_name || "";

        const city = getComponent("locality") || getComponent("sublocality");
        const state = getComponent("administrative_area_level_1");
        const country = getComponent("country");
        const zip_code =
          getComponent("postal_code") ||
          getComponent("postal_code_prefix") ||
          "";

        // Build complete street address with state
        let completeStreet = description;

        // If state exists and is not already in description, add it
        if (state && !description.includes(state)) {
          // Insert state before country if country exists in description
          if (country && description.includes(country)) {
            completeStreet = description.replace(
              country,
              `${state}, ${country}`,
            );
          } else {
            completeStreet = `${description}, ${state}`;
          }
        }

        // Set input & send to parent
        setValue(completeStreet, false);

        onSelect({
          street: completeStreet,
          city: city,
          state: state,
          country: country,
          zip_code: zip_code,
          latitude: lat,
          longitude: lng,
        });
      },
      () =>
        toast.error(t("unable_to_fetch_location")),
    );
  };

  return (
    <>
      <div style={{ position: "relative" }}>
        <div className="d-flex gap-2">
          {/* Input */}
          <input
            className="form-control address"
            disabled={disabled}
            value={inputValue}
            onChange={handleInput}
            name={name}
            placeholder="Search your address..."
          />

          {/* Current Location Button */}
          {name !== "destination_address" && (
            <button
              type="button"
              className="btn btn-dark"
              onClick={handleUseCurrentLocation}
              disabled={disabled}
              style={{ whiteSpace: "nowrap" }}
            >
              <FaMapMarkerAlt />
            </button>
          )}
        </div>

        {/* Dropdown */}
        {status === "OK" && !disabled && (
          <ul
            className="list-group position-absolute w-100 mt-1 shadow"
            style={{ zIndex: 9999 }}
          >
            {data.map(({ place_id, description }) => (
              <li
                key={place_id}
                className="list-group-item list-group-item-action"
                onClick={() => handleSelect(description)}
                style={{ cursor: "pointer" }}
              >
                {description}
              </li>
            ))}
          </ul>
        )}
      </div>

      <style jsx>{`
        .address,
        .address:focus,
        .select__control--is-focused,
        .select__control--menu-is-open,
        .css-13cymwt-control,
        .css-13cymwt-control:hover,
        .css-t3ipsp-control:hover {
          padding: 10px 12px;
          background-color: rgb(255, 255, 255);
          color: rgb(73, 80, 87);
          font-size: 14px;
          width: 100%;
          height: 55px;
          border: 1px solid #d6d6d6;
          border-radius: 12px;
          font-weight: 500;
          box-shadow: none;
        }
      `}</style>
    </>
  );
};
